import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Alert from "../../components/Alert";
import { listLedgers, createVoucher, listVouchers, deleteVoucher } from "../../api/accountApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

const VOUCHER_TYPES = ["Receipt", "Payment", "Contra", "Journal"];
const today = () => new Date().toISOString().slice(0, 10);
const emptyLine = { ledgerId: "", debitAmount: "", creditAmount: "" };

/**
 * One page replaces digicampusbd's four separate Receipt/Payment/Contra/
 * Journal voucher-entry pages plus its Transaction Invoice List / Invoice
 * Delete pages — same consolidation the backend's `createVoucher` already
 * made (see AccountTransactionController).
 */
export default function VouchersPage() {
  const [ledgers, setLedgers] = useState([]);
  const [voucherType, setVoucherType] = useState("Receipt");
  const [trnDate, setTrnDate] = useState(today());
  const [voucherNote, setVoucherNote] = useState("");
  const [lines, setLines] = useState([{ ...emptyLine }, { ...emptyLine }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [filterType, setFilterType] = useState("Receipt");
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    listLedgers().then(setLedgers);
  }, []);

  const load = () => {
    setLoading(true);
    listVouchers(filterType, fromDate, toDate)
      .then(setVouchers)
      .catch(() => setError("Failed to load vouchers."))
      .finally(() => setLoading(false));
  };

  useEffect(load, [filterType, fromDate, toDate]);

  const totalDebit = lines.reduce((sum, l) => sum + (Number(l.debitAmount) || 0), 0);
  const totalCredit = lines.reduce((sum, l) => sum + (Number(l.creditAmount) || 0), 0);
  const balanced = Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0;

  const setLine = (idx, field, value) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  };
  const addLine = () => setLines((prev) => [...prev, { ...emptyLine }]);
  const removeLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!balanced) {
      setError("Total debit must equal total credit, and be greater than zero.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        voucherType,
        trnDate,
        voucherNote,
        lines: lines
          .filter((l) => l.ledgerId && (Number(l.debitAmount) > 0 || Number(l.creditAmount) > 0))
          .map((l) => ({
            ledgerId: Number(l.ledgerId),
            debitAmount: Number(l.debitAmount) || 0,
            creditAmount: Number(l.creditAmount) || 0,
          })),
      };
      const res = await createVoucher(payload);
      setSuccess(res.message || "Voucher posted.");
      setVoucherNote("");
      setLines([{ ...emptyLine }, { ...emptyLine }]);
      if (voucherType === filterType) load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to post voucher.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (voucher) => {
    if (!window.confirm(`Delete voucher #${voucher.voucherNo}?`)) return;
    setError("");
    try {
      await deleteVoucher(voucher.trnId);
      setSuccess("Voucher deleted.");
      setVouchers((prev) => prev.filter((v) => v.trnId !== voucher.trnId));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete voucher.");
    }
  };

  const columns = [
    { key: "voucherNo", label: "Voucher No" },
    { key: "trnType", label: "Type" },
    { key: "trnDate", label: "Date" },
    { key: "trnAmount", label: "Amount" },
    { key: "voucherNote", label: "Note" },
    { key: "userName", label: "By" },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Financial Vouchers</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <form onSubmit={handleSubmit} className="user-form" style={{ marginBottom: 28 }}>
        <div className="attendance-filters">
          <label>
            Voucher Type
            <select value={voucherType} onChange={(e) => setVoucherType(e.target.value)}>
              {VOUCHER_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label>
            Date
            <input type="date" value={trnDate} onChange={(e) => setTrnDate(e.target.value)} required />
          </label>
        </div>
        <label>Note</label>
        <input value={voucherNote} onChange={(e) => setVoucherNote(e.target.value)} />

        <div className="table-scroll" style={{ marginTop: 12 }}>
        <table className="attendance-table">
          <thead>
            <tr>
              <th>Ledger</th>
              <th>Debit</th>
              <th>Credit</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, idx) => (
              <tr key={idx}>
                <td>
                  <select value={line.ledgerId} onChange={(e) => setLine(idx, "ledgerId", e.target.value)}>
                    <option value="">Select ledger</option>
                    {ledgers.map((l) => (
                      <option key={l.ledgerId} value={l.ledgerId}>
                        {l.ledgerName}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="number"
                    style={{ width: 100 }}
                    value={line.debitAmount}
                    onChange={(e) => setLine(idx, "debitAmount", e.target.value)}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    style={{ width: 100 }}
                    value={line.creditAmount}
                    onChange={(e) => setLine(idx, "creditAmount", e.target.value)}
                  />
                </td>
                <td>
                  {lines.length > 2 && (
                    <button type="button" className="btn-link danger" onClick={() => removeLine(idx)}>
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <div className="form-actions" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <button type="button" className="btn-link" onClick={addLine}>
            + Add Line
          </button>
          <span>
            Debit: {totalDebit.toFixed(2)} &nbsp; Credit: {totalCredit.toFixed(2)}{" "}
            {!balanced && <strong style={{ color: "#b91c1c" }}>(not balanced)</strong>}
          </span>
          <button type="submit" className="btn-primary" disabled={saving || !balanced}>
            {saving ? "Posting..." : `Post ${voucherType}`}
          </button>
        </div>
      </form>

      <div className="page-header">
        <h2>Voucher List</h2>
      </div>
      <div className="attendance-filters">
        <label>
          Type
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            {VOUCHER_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label>
          From
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label>
          To
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={columns}
          rows={vouchers}
          actions={(row) => (
            <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
              Delete
            </button>
          )}
        />
      )}
    </MainLayout>
  );
}
